// ============================================================
// ALBATROS — PurchaseOrdersView.tsx
// Gestión de pedidos: ordenes de compra a proveedores, sin aprobar y aprobadas.
// Mismo diseño que OrdersView (clases al-orders-*): solo cambia el contenido.
// ============================================================

import {
  Bell,
  BellRing,
  BookOpen,
  History,
  Building2,
  Clock,
  PackageSearch,
  Printer,
  RefreshCw,
  Search,
  X
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  PurchaseOrderStatus,
  PurchaseOrderSummary,
  PurchaseOrderLine,
  PurchaseOrderDetail,
  listPurchaseOrders,
  getPurchaseOrder
} from '../../services/PurchasingService';
import { getWarehouseId } from '../../services/TerminalService';
import {
  NotificationEntry,
  NotificationLog,
  addDocumentNotification,
  addLineNotification,
  addOrderNotification,
  getDocumentNotifications,
  getLineNotifications,
  getOrderNotifications,
  hasBeenNotified,
  readNotificationLog,
  withServerRows
} from '../../utils/orderNotifications';
import { clientEmailDe, customerOrderHtml, printCustomerOrder } from '../../services/OrderDocumentService';
import { listNotifications, pickupSubject, recordNotification, sendPickupNotice } from '../../services/OrderNoticeService';
import { NoticesPanel } from '../NoticesPanel/NoticesPanel';
import { EmailPromptModal } from '../EmailPromptModal/EmailPromptModal';
import { DocumentViewerModal } from '../DocumentViewerModal/DocumentViewerModal';
import { Skeleton } from '../common/Skeleton';
import './OrdersView.css';
import './PurchaseOrdersView.css';

const STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: 'Borrador',
  pending_approval: 'Pte. Aprobar',
  approved: 'Aprobado',
  partially_received: 'Recibido Parcial',
  received: 'Recibido',
  cancelled: 'Cancelado'
};

// Mismos tonos que STATUS_COLORS de la pantalla original de pedidos.
const STATUS_COLORS: Record<PurchaseOrderStatus, string> = {
  draft: '#9CA3AF',
  pending_approval: '#F97316',
  approved: '#3B82F6',
  partially_received: '#EAB308',
  received: '#22C55E',
  cancelled: '#EF4444'
};

type Filter = 'all' | PurchaseOrderStatus;

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pending_approval', label: 'Sin aprobar' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'partially_received', label: 'Recibido parcial' },
  { value: 'received', label: 'Recibidos' },
  { value: 'draft', label: 'Borrador' },
  { value: 'cancelled', label: 'Cancelados' }
];

const PAGE_SIZE = 50;

const clientLabel = (clients: string[]): string =>
  clients.length === 0 ? 'Tienda' : `${clients[0]}${clients.length > 1 ? ` +${clients.length - 1}` : ''}`;

/** Una linea esta servida cuando han llegado todos los ejemplares pedidos. */
const isLineReceived = (line: PurchaseOrderLine): boolean =>
  line.quantityOrdered > 0 && line.quantityReceived >= line.quantityOrdered;

/** Solo se avisa de lineas de cliente: las de reposicion de tienda no tienen a quien avisar. */
const isLineNotifiable = (line: PurchaseOrderLine): boolean =>
  line.clientName !== '' && isLineReceived(line);

function NotificationHistory({ entries, label = 'Avisos Anteriores Realizados' }: { entries: NotificationEntry[]; label?: string }) {
  return (
    <div className="al-orders-notif-history">
      <p className="al-orders-items-label" style={{ marginBottom: '0.5rem' }}>{label}</p>
      <div className="al-orders-notif-list">
        {entries.map((entry, i) => (
          <div key={i} className="al-orders-notif-list-item">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="al-orders-notif-dot" />
              <span className="al-orders-notif-channel">{entry.channel}</span>
            </div>
            <span className="al-orders-notif-time">
              {new Date(entry.timestamp).toLocaleString('es-ES')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PurchaseOrdersViewProps {
  showToast?: (title: string, description: string, iconType?: 'success' | 'alert') => void;
}

export default function PurchaseOrdersView({ showToast }: PurchaseOrdersViewProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [tableSearch, setTableSearch] = useState('');
  const [orders, setOrders] = useState<PurchaseOrderSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrderDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const [notifLog, setNotifLog] = useState<NotificationLog>(() => readNotificationLog());
  const [notificationPendingLine, setNotificationPendingLine] = useState<PurchaseOrderLine | null>(null);
  const [notificationPendingOrder, setNotificationPendingOrder] = useState<PurchaseOrderDetail | null>(null);

  const requestId = useRef(0);
  /** Panel lateral con el historial de avisos del pedido abierto. */
  const [showNotices, setShowNotices] = useState(false);
  // El envio es asincrono (y a veces espera a que el cajero teclee el email):
  // se escribe sobre el historial vigente, no sobre el de cuando se pulso.
  const notifLogRef = useRef(notifLog);
  notifLogRef.current = notifLog;
  /** Aviso a la espera de un email, porque el cliente no tiene ninguno en su ficha. */
  const [noticeTarget, setNoticeTarget] = useState<{ clientName: string; send: (email: string) => void } | null>(null);
  // Con almacén asignado solo se piden los pedidos de ese almacén; sin él, todos.
  const hasWarehouse = getWarehouseId() !== '';

  useEffect(() => {
    const id = ++requestId.current;
    setIsLoading(true);
    setError(null);

    listPurchaseOrders({ status: filter === 'all' ? undefined : filter, pageSize: PAGE_SIZE })
      .then(result => {
        if (id !== requestId.current) return;
        setOrders(result.orders);
        setTotal(result.total);
        setIsLoading(false);
      })
      .catch(err => {
        if (id !== requestId.current) return;
        setOrders([]);
        setTotal(0);
        setError(err?.message || 'No se pudieron cargar los pedidos.');
        setIsLoading(false);
      });
  }, [filter, reloadKey]);

  /** Todos los avisos del pedido (completo y por linea), del mas antiguo al mas reciente. */
  const allNotices = (order: PurchaseOrderDetail): NotificationEntry[] => {
    const all: NotificationEntry[] = [...getOrderNotifications(notifLog, order.id)];
    order.lines.forEach(l => {
      getLineNotifications(notifLog, order.id, l.id).forEach(e => {
        all.push({ ...e, channel: `${l.title}: ${e.channel}` });
      });
    });
    return all.sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0));
  };

  /** Trae del backend los avisos del pedido; con `warn`, avisa si no se pueden leer. */
  const refreshNotifications = (orderId: string, warn = false) =>
    listNotifications(orderId)
      .then(rows => setNotifLog(prev => withServerRows(prev, orderId, rows)))
      .catch(err => {
        if (warn) showToast?.('Avisos', (err as any)?.message || 'No se pudo cargar el historial de avisos.', 'alert');
      });

  const openDetail = (order: PurchaseOrderSummary) => {
    setIsLoadingDetail(true);
    // Se enseña ya la cabecera mientras llegan las lineas.
    setSelectedOrder({ ...order, lines: [], history: [], notes: '', paymentTerms: '' });
    refreshNotifications(order.id, true);
    getPurchaseOrder(order.id)
      .then(full => { setSelectedOrder(full); setIsLoadingDetail(false); })
      .catch(err => {
        setIsLoadingDetail(false);
        showToast?.('Pedido', err?.message || 'No se pudo cargar el detalle del pedido.', 'alert');
      });
  };

  const closeDetail = () => {
    setShowNotices(false);
    setSelectedOrder(null);
    setNotificationPendingLine(null);
    setNotificationPendingOrder(null);
  };

  // ── Aviso de recogida al cliente ─────────────────────────
  // El aviso sale por correo. Se apunta en el historial local solo si el envio
  // ha funcionado. Sin email en la ficha del cliente, se pregunta en el momento.
  // El historial sigue siendo de este navegador: el backend no lo registra.

  const askNoticeEmail = async (clientName: string, send: (email: string) => void) => {
    const fichaEmail = await clientEmailDe(clientName);
    if (fichaEmail) {
      send(fichaEmail);
    } else {
      setNoticeTarget({ clientName, send });
    }
  };

  /**
   * Manda el correo y deja constancia en el backend, salga o no. Si el correo
   * ha salido y lo que falla es la constancia, se avisa aparte: reenviarlo
   * duplicaria el aviso al cliente.
   */
  const deliverNotice = async (o: {
    orderId: string;
    orderNumber: string;
    lineId?: string;
    email: string;
    clientName: string;
    titles: string[];
    wholeOrder?: boolean;
    okMessage: string;
  }) => {
    let failure = '';
    try {
      await sendPickupNotice({
        to: o.email,
        clientName: o.clientName,
        orderNumber: o.orderNumber,
        titles: o.titles,
        wholeOrder: o.wholeOrder
      });
    } catch (err) {
      failure = (err as any)?.message || 'No se pudo enviar el correo.';
    }

    try {
      await recordNotification({
        orderId: o.orderId,
        lineId: o.lineId,
        recipient: o.email,
        subject: pickupSubject(o.orderNumber),
        error: failure
      });
      await refreshNotifications(o.orderId);
    } catch (err) {
      showToast?.(
        'Aviso sin registrar',
        `${failure ? 'El correo no salió y ' : 'El correo salió, pero '}no se pudo guardar el aviso en el historial: ${(err as any)?.message || 'error desconocido'}.`,
        'alert'
      );
      if (!failure) return;
    }

    if (failure) {
      showToast?.('Aviso no enviado', failure, 'alert');
    } else {
      showToast?.('Aviso de Recogida', o.okMessage, 'alert');
    }
  };

  const notifyLine = (orderId: string, line: PurchaseOrderLine) => {
    const orderNumber = selectedOrder ? selectedOrder.number : '';
    return askNoticeEmail(line.clientName, email => deliverNotice({
      orderId,
      orderNumber,
      lineId: line.id,
      email,
      clientName: line.clientName,
      titles: [line.title],
      okMessage: `Correo enviado a ${email} para retirar '${line.title}'.`
    }));
  };

  const notifyWholeOrder = (order: PurchaseOrderDetail) => {
    const clientName = order.clients[0] || '';
    return askNoticeEmail(clientName, email => deliverNotice({
      orderId: order.id,
      orderNumber: order.number,
      email,
      clientName,
      titles: order.lines.filter(isLineNotifiable).map(l => l.title),
      wholeOrder: true,
      okMessage: `Correo enviado a ${email}: pedido completo y listo para recoger.`
    }));
  };

  /** Si ya se le aviso antes, pedir confirmacion; si no, avisar directamente. */
  const handleRequestLineNotification = (orderId: string, line: PurchaseOrderLine) => {
    if (hasBeenNotified(getLineNotifications(notifLog, orderId, line.id))) {
      setNotificationPendingLine(line);
    } else {
      notifyLine(orderId, line);
    }
  };

  const handleRequestOrderNotification = (order: PurchaseOrderDetail) => {
    if (hasBeenNotified(getOrderNotifications(notifLog, order.id))) {
      setNotificationPendingOrder(order);
    } else {
      notifyWholeOrder(order);
    }
  };

  const needle = tableSearch.trim().toLowerCase();
  const filteredOrders = orders.filter(o =>
    !needle ||
    o.number.toLowerCase().indexOf(needle) !== -1 ||
    o.supplierName.toLowerCase().indexOf(needle) !== -1 ||
    o.createdByName.toLowerCase().indexOf(needle) !== -1 ||
    o.clients.some(c => c.toLowerCase().indexOf(needle) !== -1)
  );

  const clientLines = selectedOrder ? selectedOrder.lines.filter(l => l.clientName !== '') : [];
  // Solo se avisa del pedido entero cuando es de un unico cliente: una orden de
  // compra puede agrupar lineas de varios, y el prototipo asumia uno por pedido.
  const canNotifyWholeOrder = !!selectedOrder &&
    selectedOrder.status === 'received' &&
    selectedOrder.clients.length === 1 &&
    clientLines.length > 0;

  // El resguardo es de UN cliente: con varios en la misma orden no se sabe de
  // cual, y mezclarlos le ensenaria a uno lo que ha pedido otro.
  const orderClient = selectedOrder && selectedOrder.clients.length === 1 ? selectedOrder.clients[0] : '';

  /** Resguardo del pedido abierto en pantalla; null = visor cerrado. */
  const [orderDoc, setOrderDoc] = useState<{ id: string; number: string; clientName: string; statusLabel: string } | null>(null);

  const openOrderDoc = () => {
    if (!selectedOrder || !orderClient) return;
    setOrderDoc({
      id: selectedOrder.id,
      number: selectedOrder.number,
      clientName: orderClient,
      statusLabel: STATUS_LABELS[selectedOrder.status]
    });
  };

  /** Pedido cuyo resguardo se va a mandar por email, con el email de su ficha ya buscado. */
  const [emailTarget, setEmailTarget] = useState<{ id: string; number: string; clientName: string; fichaEmail: string } | null>(null);

  const askOrderEmail = async () => {
    if (!orderDoc) return;
    // La ficha puede tardar: se pregunta antes de abrir el modal para que salga
    // ya con la direccion puesta y el cajero solo confirme.
    const fichaEmail = await clientEmailDe(orderDoc.clientName);
    setEmailTarget({ id: orderDoc.id, number: orderDoc.number, clientName: orderDoc.clientName, fichaEmail });
  };

  const sendOrderDoc = (email: string) => {
    if (!emailTarget) return;
    setNotifLog(addDocumentNotification(notifLog, emailTarget.id, email));
    showToast?.(
      'Resguardo enviado',
      `Resguardo del pedido ${emailTarget.number} enviado a ${email}.`,
      'success'
    );
    setEmailTarget(null);
  };

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="al-orders">

      {/* Header */}
      <div className="al-orders-header">
        <div>
          <h2 className="al-orders-title">Gestión de Pedidos</h2>
          <p className="al-orders-subtitle">
            {hasWarehouse
              ? 'Pedidos a proveedores de este almacén'
              : 'Pedidos a proveedores de todos los almacenes (esta caja no tiene almacén asignado)'}
          </p>
        </div>
        <div className="po-header-right">
          <div className="al-orders-search-wrap">
            <Search className="al-orders-search-icon" size={20} />
            <input
              type="text"
              placeholder="Buscar por Nº, Cliente o Proveedor..."
              className="al-orders-search-input"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
          </div>
          <button onClick={() => setReloadKey(k => k + 1)} className="po-refresh" title="Actualizar" aria-label="Actualizar">
            <RefreshCw size={18} className={isLoading ? 'po-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filtros por estado */}
      <div className="po-filters">
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`po-filter${filter === f.value ? ' po-filter--active' : ''}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="al-orders-table-wrap">
        <table className="al-orders-table">
          <thead>
            <tr>
              <th>ID Pedido</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Proveedor</th>
              <th>Estado General</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j}><Skeleton width="80%" height="1rem" /></td>
                  ))}
                </tr>
              ))
            ) : filteredOrders.length > 0 ? (
              filteredOrders.map(order => (
                <tr key={order.id} onClick={() => openDetail(order)} className="al-orders-row">
                  <td>
                    <span className="al-orders-id">{order.number}</span>
                  </td>
                  <td>
                    <p className="al-orders-date">{order.createdAt.toLocaleDateString()}</p>
                    <p className="al-orders-time">
                      {order.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} title={order.clients.join(', ')}>
                      <div className="al-orders-client-icon">
                        <Building2 size={16} />
                      </div>
                      <span className={`al-orders-client-name${order.clients.length === 0 ? ' po-client-none' : ''}`}>
                        {clientLabel(order.clients)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="al-orders-client-name">{order.supplierName || '—'}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div className="al-orders-status-dot" style={{ background: STATUS_COLORS[order.status] }} />
                      <span className="al-orders-status-label " style={{ color: STATUS_COLORS[order.status] }}>
                        {STATUS_LABELS[order.status]}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="al-orders-empty">
                  <PackageSearch size={64} style={{ opacity: 0.1, marginBottom: '1rem' }} />
                  <p>{error || 'No se encontraron pedidos'}</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && !error && total > orders.length && (
        <p className="po-more">Mostrando los {orders.length} más recientes de {total}.</p>
      )}

      {/* ── Order Detail Modal ── */}
      {selectedOrder && (
        <div className="al-modal-root">
          <div className="al-modal-backdrop" onClick={closeDetail} />
          <div className="al-modal-card al-modal-card--xl al-modal-card--full">

            {/* Header */}
            <div className="al-orders-detail-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                  <span className="al-orders-id-detail">{selectedOrder.number}</span>
                  <h3 className="al-orders-detail-title">Detalle del Pedido</h3>
                </div>
                <p className="al-orders-subtitle">
                  {clientLabel(selectedOrder.clients)} • {selectedOrder.createdAt.toLocaleDateString()}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                  <p className="al-orders-select-label">Estado del Pedido</p>
                  <span className="al-orders-status-select" style={{ background: STATUS_COLORS[selectedOrder.status], cursor: 'default' }}>
                    {STATUS_LABELS[selectedOrder.status]}
                  </span>
                </div>

                {/*
                  Resguardo para el cliente: su copia del pedido, con el numero
                  OC y lo que pago. Solo sus lineas, no la orden entera.
                */}
                <button
                  onClick={openOrderDoc}
                  disabled={!orderClient}
                  className={`al-orders-notify-btn ${orderClient ? 'al-orders-notify-btn--active' : 'al-orders-notify-btn--disabled'}`}
                  title={orderClient
                    ? `Resguardo del pedido para ${orderClient}`
                    : selectedOrder.clients.length > 1
                      ? 'Este pedido tiene varios clientes: no se puede sacar un resguardo común.'
                      : 'Este pedido es de reposición de tienda: no hay cliente al que darle resguardo.'}
                >
                  <Printer size={24} />
                </button>

                {canNotifyWholeOrder ? (
                  <button
                    onClick={() => handleRequestOrderNotification(selectedOrder)}
                    className="al-orders-notify-btn al-orders-notify-btn--active"
                    title="¡Pedido completo! Avisar al cliente"
                  >
                    <BellRing size={24} />
                  </button>
                ) : (
                  <button
                    disabled
                    className="al-orders-notify-btn al-orders-notify-btn--disabled"
                    title={selectedOrder.clients.length > 1
                      ? 'Este pedido tiene varios clientes: avisa a cada uno desde la campana de su línea.'
                      : 'El pedido debe estar recibido por completo para avisar al cliente.'}
                  >
                    <Bell size={24} />
                  </button>
                )}

                <button
                  onClick={() => setShowNotices(true)}
                  className="al-orders-notify-btn al-orders-notify-btn--active"
                  title={`Historial de avisos al cliente (${allNotices(selectedOrder).length})`}
                  data-testid="open-notices"
                >
                  <History size={24} />
                </button>

                <button onClick={closeDetail} className="al-orders-close-btn">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Items */}
            <div className="al-orders-detail-body al-scrollbar-hide">
              <p className="al-orders-items-label">Libros incluidos en este pedido</p>
              {isLoadingDetail ? (
                <Skeleton width="100%" height="4rem" />
              ) : selectedOrder.lines.length === 0 ? (
                <p className="al-orders-item-isbn">Este pedido no tiene líneas.</p>
              ) : (
                selectedOrder.lines.map(line => (
                  <div key={line.id} className="al-orders-item">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                      <div className="al-orders-item-icon">
                        <BookOpen size={24} />
                      </div>
                      <div>
                        <h5 className="al-orders-item-title">{line.title}</h5>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                          <span className="al-orders-item-source">
                            <Clock size={12} /> {selectedOrder.supplierName || 'Sin proveedor'}
                          </span>
                          {line.clientName && (
                            <span className="al-orders-item-source">
                              <Building2 size={12} /> Para: {line.clientName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span className="al-orders-count">
                        {line.quantityReceived}/{line.quantityOrdered} recibidas
                      </span>

                      <div className="al-orders-divider" />

                      {isLineNotifiable(line) ? (
                        <button
                          onClick={() => handleRequestLineNotification(selectedOrder.id, line)}
                          className="al-orders-notify-btn al-orders-notify-btn--active"
                          title="Avisar al cliente: ¡Listo para recoger!"
                        >
                          <BellRing size={20} />
                        </button>
                      ) : (
                        <button
                          disabled
                          className="al-orders-notify-btn al-orders-notify-btn--disabled"
                          title={line.clientName === ''
                            ? 'Línea de reposición de tienda: no hay cliente al que avisar.'
                            : 'Faltan ejemplares por recibir para poder avisar al cliente.'}
                        >
                          <Bell size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}

              {getDocumentNotifications(notifLog, selectedOrder.id).length > 0 && (
                <NotificationHistory
                  entries={getDocumentNotifications(notifLog, selectedOrder.id)}
                  label="Resguardos Enviados"
                />
              )}

              {selectedOrder.history.length > 0 && (
                <>
                  <p className="al-orders-items-label">Seguimiento</p>
                  {selectedOrder.history.map((h, i) => (
                    <p key={i} className="al-orders-item-isbn">
                      {STATUS_LABELS[h.status]} • {h.changedAt.toLocaleDateString()} {h.changedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {h.changedByName ? ` • ${h.changedByName}` : ''}{h.note ? ` — ${h.note}` : ''}
                    </p>
                  ))}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="al-orders-detail-footer">
              <button onClick={closeDetail} className="al-orders-confirm-btn">
                Cerrar Detalle
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Resguardo del pedido para el cliente ── */}
      {orderDoc && (
        <DocumentViewerModal
          kind="order"
          title={`Pedido ${orderDoc.number}`}
          subtitle={orderDoc.clientName}
          load={() => customerOrderHtml(orderDoc.id, orderDoc.clientName, orderDoc.statusLabel)}
          onPrint={() => printCustomerOrder(orderDoc.id, orderDoc.clientName, orderDoc.statusLabel)}
          onEmail={askOrderEmail}
          onClose={() => setOrderDoc(null)}
        />
      )}

      {emailTarget && (
        <EmailPromptModal
          title="Enviar Resguardo por Email"
          clientName={emailTarget.clientName}
          fichaEmail={emailTarget.fichaEmail}
          confirmLabel="ENVIAR AHORA"
          hint="Se usará solo para este envío. La ficha del cliente no cambia."
          onClose={() => setEmailTarget(null)}
          onConfirm={sendOrderDoc}
        />
      )}

      {selectedOrder && showNotices && (
        <NoticesPanel
          title="Avisos al Cliente"
          subtitle={`${selectedOrder.number} • ${clientLabel(selectedOrder.clients)}`}
          items={allNotices(selectedOrder)}
          onClose={() => setShowNotices(false)}
        />
      )}

      {noticeTarget && (
        <EmailPromptModal
          title="Enviar Aviso por Email"
          clientName={noticeTarget.clientName}
          confirmLabel="ENVIAR AVISO"
          hint="El cliente no tiene email en su ficha. Se usará solo para este aviso y la ficha no cambia."
          onClose={() => setNoticeTarget(null)}
          onConfirm={(email) => {
            const { send } = noticeTarget;
            setNoticeTarget(null);
            send(email);
          }}
        />
      )}

      {/* ── Reconfirmación de aviso de una línea ── */}
      {selectedOrder && notificationPendingLine && (
        <div className="al-modal-root" style={{ zIndex: 250 }}>
          <div className="al-modal-backdrop" onClick={() => setNotificationPendingLine(null)} />
          <div className="al-modal-card al-modal-card--md" style={{ padding: '2rem' }}>

            <div className="al-orders-notif-header">
              <div className="al-orders-notify-icon">
                <BellRing size={24} />
              </div>
              <div>
                <h4 className="al-orders-confirm-title">Cliente ya Notificado</h4>
                <p className="al-orders-subtitle">Historial encontrado de avisos</p>
              </div>
            </div>

            <div className="al-orders-notif-book" style={{ marginBottom: '1.5rem' }}>
              <p className="al-orders-items-label">Libro a Recoger</p>
              <p className="al-orders-item-title">{notificationPendingLine.title}</p>
              <p className="al-orders-item-isbn" style={{ marginTop: '0.25rem' }}>
                Para: {notificationPendingLine.clientName}
              </p>
            </div>

            <NotificationHistory
              entries={getLineNotifications(notifLog, selectedOrder.id, notificationPendingLine.id)}
            />

            <p style={{ fontSize: '0.75rem', color: '#737373', marginBottom: '1.5rem', lineHeight: 1.5, marginTop: '1.5rem' }}>
              Este cliente ya ha sido avisado previamente. ¿Estás seguro de que deseas enviar una nueva notificación? Se registrará como un nuevo intento de contacto en el historial.
            </p>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setNotificationPendingLine(null)} className="al-orders-btn-secondary">
                Cancelar
              </button>
              <button
                onClick={() => {
                  notifyLine(selectedOrder.id, notificationPendingLine);
                  setNotificationPendingLine(null);
                }}
                className="al-orders-btn-primary"
              >
                Confirmar Envío
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reconfirmación de aviso de pedido completo ── */}
      {notificationPendingOrder && (
        <div className="al-modal-root" style={{ zIndex: 250 }}>
          <div className="al-modal-backdrop" onClick={() => setNotificationPendingOrder(null)} />
          <div className="al-modal-card al-modal-card--md" style={{ padding: '2rem' }}>

            <div className="al-orders-notif-header">
              <div className="al-orders-notify-icon">
                <BellRing size={24} />
              </div>
              <div>
                <h4 className="al-orders-confirm-title">Pedido ya Notificado</h4>
                <p className="al-orders-subtitle">Aviso de Disponibilidad Total</p>
              </div>
            </div>

            <div className="al-orders-notif-book" style={{ marginBottom: '1.5rem' }}>
              <p className="al-orders-items-label">Pedido Completado</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                <span className="al-orders-id">{notificationPendingOrder.number}</span>
                <span style={{ fontSize: '0.625rem', fontWeight: 900, color: 'var(--al-gray-400)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  {notificationPendingOrder.lines.filter(isLineNotifiable).length} libros recibidos
                </span>
              </div>
              <p className="al-orders-item-isbn" style={{ marginTop: '0.5rem' }}>
                Cliente: <strong style={{ color: 'var(--al-orange)', textTransform: 'uppercase' }}>
                  {clientLabel(notificationPendingOrder.clients)}
                </strong>
              </p>
            </div>

            <NotificationHistory entries={getOrderNotifications(notifLog, notificationPendingOrder.id)} />

            <p style={{ fontSize: '0.75rem', color: '#737373', marginBottom: '1.5rem', lineHeight: 1.5, marginTop: '1.5rem' }}>
              Este cliente ya ha sido avisado previamente de que su pedido está totalmente completo. ¿Estás seguro de que deseas enviar una nueva notificación? Se registrará como un nuevo intento de contacto en el historial.
            </p>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setNotificationPendingOrder(null)} className="al-orders-btn-secondary">
                Cancelar
              </button>
              <button
                onClick={() => {
                  notifyWholeOrder(notificationPendingOrder);
                  setNotificationPendingOrder(null);
                }}
                className="al-orders-btn-primary"
              >
                Confirmar Envío
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
